import { Box, Grid, GridItem, Heading, Text } from '@chakra-ui/react'
import { DashboardLoading } from 'components/atoms/DashboardLoading'
import { DashboardNotFound } from 'components/atoms/DashboardNotFound'
import { CustomPieChart } from 'components/molecules/CustomPieChart'
import { useInstitutionsContext } from 'contexts/institutions'
import { ReportManagementQueryDTO } from 'models/ReportQueryDTO'
import { useEffect, useRef, useState } from 'react'
import { getDashboardsManagementReports } from 'services/report'
import formatter from 'utils/formatter'
import { removeEmptyValues } from 'utils/removeEmptyValues'
import { initialFiltersManagementReport } from '..'
import { Filter, FilterRef } from '../../Filter/Filter'
import { FilterManagementReport } from '../../Filter/interfaces'
import { DashboardItem } from './interfaces'

interface Props {
    onFilter: (filter: FilterManagementReport) => void
    idDashboard: string
}

export const ModeDashboard: React.FC<Props> = ({ onFilter, idDashboard }) => {
    const [dataDashboards, setDataDashboard] = useState<DashboardItem[]>([])
    const [filter, setFilter] = useState<FilterManagementReport>(
        initialFiltersManagementReport
    )
    const [loading, setLoading] = useState<boolean>(true)

    const filterRef = useRef<FilterRef>(null)
    const { currentAgent, currentInstitution, institutions, agents } =
        useInstitutionsContext()

    const handleFilter = (filterData: FilterManagementReport) => {
        onFilter(filterData)
        setFilter(filterData)
        fetchDashboard(filterData)
    }

    const buildQuery = (query: any): ReportManagementQueryDTO => {
        const cnpjAgente = currentAgent?.cnpj ?? ''
        removeEmptyValues(query)
        return query ? { ...query, cnpjAgente } : { cnpjAgente }
    }

    const formatPayloadFilter = (
        value: FilterManagementReport
    ): FilterManagementReport => {
        if (!value) return {} as FilterManagementReport

        let payload = {} as FilterManagementReport

        Object.keys(value).forEach(key => {
            payload = {
                ...payload,
                [key]: value[key as keyof FilterManagementReport] ?? ''
            }
        })

        if (value?.periodoFinal || value?.periodoInicial) {
            payload = {
                ...payload,
                periodoFinal: value.periodoFinal
                    ? formatter.formatDateToPayload(value.periodoFinal)
                    : '',
                periodoInicial: value.periodoInicial
                    ? formatter.formatDateToPayload(value.periodoInicial)
                    : ''
            }
        }
        return payload
    }

    const fetchDashboard = async (query: FilterManagementReport) => {
        setLoading(true)
        try {
            const filterParams = formatPayloadFilter(query)
            onFilter(filterParams)

            const {
                data: { dados }
            } = await getDashboardsManagementReports(
                buildQuery(filterParams),
                currentInstitution?.id
            )

            setDataDashboard(dados)
        } catch (error) {
            console.error('error service dashboard')
        } finally {
            setLoading(false)
        }
    }

    const setDataCacheFilter = () => {
        if (filterRef.current && filter) {
            filterRef.current.setValues(filter)
        }
    }

    useEffect(() => {
        if (
            currentInstitution?.id !== undefined ||
            currentAgent?.razaoSocial !== undefined
        ) {
            setDataCacheFilter()
            fetchDashboard(filter ?? {})
        }
    }, [currentInstitution?.id, currentAgent?.razaoSocial])

    const CustomTooltipQuantity = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const { nome, quantidade } = payload[0].payload
            return (
                <Box
                    bg="white"
                    border="1px solid #ddd"
                    p={4}
                    borderRadius="md"
                    shadow="lg"
                    color="gray.700"
                >
                    <Text fontWeight="bold" mb={2}>
                        {nome}
                    </Text>
                    <Box display="flex" justifyContent="space-between">
                        <Text>Quantidade: </Text>
                        <Text fontWeight="bold" color="blue.600">
                            {formatter.formatWithLeadingZero(quantidade)}
                        </Text>
                    </Box>
                </Box>
            )
        }
        return null
    }

    return (
        <>
            <Filter handleFilter={handleFilter} ref={filterRef} />

            {loading ? (
                <DashboardLoading />
            ) : (
                <div id={idDashboard}>
                    <Box mt={8}>
                        {dataDashboards.length > 0 ? (
                            <Grid
                                templateColumns="repeat(auto-fit, minmax(500px, 1fr))"
                                gap={6}
                            >
                                {dataDashboards.map(
                                    (item: DashboardItem, index: number) => (
                                        <GridItem key={index}>
                                            <Box
                                                bg="white"
                                                borderRadius="md"
                                                shadow="lg"
                                                p={6}
                                                transition="all 0.3s"
                                                _hover={{ shadow: 'xl' }}
                                            >
                                                <Heading
                                                    size="md"
                                                    textAlign="center"
                                                    mb={4}
                                                >
                                                    {item.nome}
                                                </Heading>
                                                <CustomPieChart
                                                    dataKey="quantidade"
                                                    outerRadius={150}
                                                    values={item.values}
                                                    children={
                                                        CustomTooltipQuantity
                                                    }
                                                />
                                            </Box>
                                        </GridItem>
                                    )
                                )}
                            </Grid>
                        ) : (
                            <DashboardNotFound />
                        )}
                    </Box>
                </div>
            )}
        </>
    )
}
